import { FileIcon } from '../Shared/FileIcon';

interface ICitationsProps {
    citations?: Array<{ ref: string; index: number; title: string }>;
    contextCitationsIndexes?: number[];
}

export const Citations: React.FC<ICitationsProps> = ({ citations, contextCitationsIndexes }) => {
    // if there are no citations, don't render anything
    if (!citations || citations.length === 0) {
        return null;
    }

    // citations is an array of strings which are links to the sources of the information
    // we need to get the domain name of each link and display it in a list
    // for example, if the citation is https://www.cdc.gov/coronavirus/2019-ncov/vaccines/fully-vaccinated.html
    // we want to display "cdc.gov" in the list with link as the href
    const citationList: JSX.Element[] = [];

    citations.forEach((citation, index) => {
        let url;
        let isValidUrl = true;

        try {
            url = new URL(citation.ref);
        } catch (e) {
            isValidUrl = false;
        }

        if (isValidUrl && url) {
            const domain = url.hostname.replace('www.', '');

            if (domain.includes('sharepoint.com') || domain.includes('blob.core.windows.net')) {
                let documentName = url.pathname.split('/').pop();
                if (url.searchParams.has('file')) {
                    documentName = url.searchParams.get('file') as string;
                }

                if (!documentName) {
                    return;
                }

                const decodedDocumentName = decodeURIComponent(documentName);
                const documentNameSliced = decodedDocumentName.slice(0, 23);

                citationList.push(
                    <>
                        <span className="px-2 py-2 bg-primary-800 text-white text-xs shadow rounded-l-md">
                            {citation.index + 1}
                        </span>
                        <span className="text-xs mr-2 py-2 pl-1 pr-2 bg-primary-600 rounded-r-md shadow text-white">
                            <a
                                key={index}
                                title={decodedDocumentName}
                                target={'_blank'}
                                href={citation.ref}
                                onClick={undefined}>
                                <FileIcon fileNameOrURL={citation.ref} />
                                {contextCitationsIndexes?.includes(index)
                                    ? `${index + 1}. ${documentNameSliced}`
                                    : documentNameSliced}
                                {documentNameSliced !== decodedDocumentName && '...'}
                            </a>
                        </span>
                    </>,
                );
            } else {
                citationList.push(
                    <>
                        <span className="px-2 py-2 bg-primary-800 text-white text-xs shadow rounded-l-md">
                            {citation.index + 1}
                        </span>
                        <span className="text-xs mr-2 py-2 pl-1 pr-2 bg-primary-600 rounded-r-md shadow text-white">
                            <a href={citation.ref} target="_blank" key={index}>
                                <FileIcon fileNameOrURL={'https'} />
                                {domain}
                            </a>
                        </span>
                    </>,
                );
            }
        } else {
            let fileName = citation.title;
            fileName = fileName.replace(/\[Text\]\.txt|\[Table\d+\]\.txt/g, ''); // Remove Table Chunk references
            const fileNameSliced = fileName.slice(0, 23);

            citationList.push(
                <>
                    <span className="px-2 py-2 bg-primary-800 text-white text-xs shadow rounded-l-md">
                        {citation.index + 1}
                    </span>
                    <span className="text-xs mr-2 py-2 pl-1 pr-2 bg-primary-600 rounded-r-md shadow text-white">
                        <a key={index} title={fileName}>
                            <FileIcon fileNameOrURL={fileName} />
                            {contextCitationsIndexes?.includes(index)
                                ? `${index + 1}. ${fileNameSliced}`
                                : fileNameSliced}
                            {fileNameSliced !== fileName && '...'}
                        </a>
                    </span>
                </>,
            );
        }
    });

    return (
        <div className="bg-gray-100 mt-8 rounded dark:bg-slate-900 w-full block">
            <details className="dark:bg-gray-900 rounded-lg p-1 w-full">
                <summary className="cursor-pointer py-2 px-3 font-semibold text-sm dark:text-white w-full">
                    Response citations*
                </summary>
                <div className="mt-2 mx-4 mb-3 text-sm leading-tight dark:text-white w-auto inline-block transition-all duration-200">
                    <p>Here is a list of citations used to generate the response above:</p>
                    <div className="flex flex-row flex-start flex-wrap my-4 gap-1 w-full">
                        {citationList.map((citation, index) => (
                            <div key={index} className="mb-4">
                                {citation}
                            </div>
                        ))}
                    </div>
                    <div className="italic w-full">
                        * This response was generated using a combination of internet sources and Azure ChatGPT.
                    </div>
                </div>
            </details>
        </div>
    );
};
